import React,{useState,useEffect,useCallback}from'react';
import Layout from'../components/layout/Layout';
import {useTranslation} from 'react-i18next';

const BASE=process.env.REACT_APP_API_URL;
const token=()=>localStorage.getItem('accessToken')||localStorage.getItem('token')||'';
const hdr=()=>({'Content-Type':'application/json','Authorization':`Bearer ${token()}`});
const apiFetch=async(path:string,method='GET',body?:any)=>{
  const r=await fetch(`${BASE}${path}`,{method,headers:hdr(),body:body?JSON.stringify(body):undefined});
  return r.json().catch(()=>({}));
};

const INP=({value,onChange,type='text',placeholder='',disabled=false}:any)=>(
  <input type={type} value={value} onChange={onChange} placeholder={placeholder} disabled={disabled}
    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#015CA6]"/>
);
const SEL=({value,onChange,children}:any)=>(
  <select value={value} onChange={onChange} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none">{children}</select>
);
const FLD=({label,children,required=false}:any)=>(
  <div><label className="block text-xs font-semibold text-gray-600 mb-1">{label}{required&&<span className="text-red-500 ml-0.5">*</span>}</label>{children}</div>
);
const BADGE=({label,color='blue'}:any)=>{
  const c:any={blue:'bg-blue-50 text-[#02357A]',green:'bg-green-100 text-green-700',
    red:'bg-red-100 text-red-700',yellow:'bg-yellow-100 text-yellow-700',
    gray:'bg-gray-100 text-gray-500',purple:'bg-purple-100 text-purple-700',
    teal:'bg-teal-100 text-[#039089]'};
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c[color]||c.blue}`}>{label}</span>;
};

const STATUS_COLOR:any={
  CLEAN:'green',DIRTY:'red',STERILIZED:'teal',IN_USE:'yellow',
  QUARANTINE:'red',PENDING:'yellow',COMPLETE:'green',FAILED:'red',
  AVAILABLE:'green',DISPATCHED:'blue',RETURNED:'gray',
};

const STERILIZATION_METHODS=['AUTOCLAVE','EO_GAS','PLASMA','DRY_HEAT','CHEMICAL','RADIATION'];
const TRAY_STATUSES=['CLEAN','DIRTY','STERILIZED','IN_USE','QUARANTINE','UNDER_REPAIR'];
const INSTRUMENT_CATEGORIES=['SURGICAL','DIAGNOSTIC','LAPAROSCOPIC','ORTHOPAEDIC','GYNAECOLOGY','ENT','OPHTHALMIC','DENTAL','CARDIAC','NEURO','GENERAL'];

export default function CssdPage(){
  const {t}=useTranslation();
  const [tab,setTab]=useState<'dashboard'|'instruments'|'sets'|'trays'|'sterilization'|'autoclaves'|'dispatch'|'tracking'>('dashboard');
  const [instruments,setInstruments]=useState<any[]>([]);
  const [sets,setSets]=useState<any[]>([]);
  const [trays,setTrays]=useState<any[]>([]);
  const [sterilizations,setSterilizations]=useState<any[]>([]);
  const [autoclaves,setAutoclaves]=useState<any[]>([]);
  const [dispatches,setDispatches]=useState<any[]>([]);
  const [tracking,setTracking]=useState<any[]>([]);
  const [cycles,setCycles]=useState<any[]>([]);
  const [loading,setLoading]=useState(false);
  const [msg,setMsg]=useState('');
  const [search,setSearch]=useState('');
  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),3000);};

  // Forms
  const [showForm,setShowForm]=useState(false);
  const [editItem,setEditItem]=useState<any>(null);
  const [formType,setFormType]=useState('');

  const defInstrument={instrumentCode:'',instrumentName:'',category:'SURGICAL',material:'STAINLESS_STEEL',totalCount:'1',isActive:true,description:''};
  const defSet={setCode:'',setName:'',procedureType:'',sterilizationMethod:'AUTOCLAVE',isActive:true,instruments:[]};
  const defTray={setId:'',trayCode:'',trayName:'',status:'CLEAN',currentLocation:'CSSD',notes:''};
  const defSterilization={trayId:'',autoclaveId:'',cycleNumber:'',loadNumber:'',sterilizationDate:new Date().toISOString().slice(0,16),expiryDate:'',performedBy:''};
  const defAutoclave={autoclaveCode:'',autoclaveName:'',brand:'',capacityUnits:'',lastMaintenance:'',nextMaintenance:'',isActive:true};
  const defDispatch={trayId:'',dispatchedTo:'',requiredBy:'',procedureId:'',notes:''};
  const defCycle={autoclaveId:'',cycleNumber:'',cycleDate:new Date().toISOString().slice(0,16),temperature:'134',pressure:'2.2',durationMinutes:'18',cycleResult:'PASS',notes:''};

  const [form,setForm]=useState<any>({});
  const f=(k:string,v:any)=>setForm((x:any)=>({...x,[k]:v}));

  const load=useCallback(async()=>{
    setLoading(true);
    const[ins,st,tr,ster,ac,dis,cyc]=await Promise.all([
      apiFetch('/enterprise/cssd/instruments'),
      apiFetch('/enterprise/cssd/sets'),
      apiFetch('/enterprise/cssd/trays'),
      apiFetch('/enterprise/cssd/sterilization'),
      apiFetch('/enterprise/cssd/autoclaves'),
      apiFetch('/enterprise/cssd/dispatch'),
      apiFetch('/enterprise/cssd/cycles'),
    ]);
    setInstruments(Array.isArray(ins)?ins:ins?.data||[]);
    setSets(Array.isArray(st)?st:st?.data||[]);
    setTrays(Array.isArray(tr)?tr:tr?.data||[]);
    setSterilizations(Array.isArray(ster)?ster:ster?.data||[]);
    setAutoclaves(Array.isArray(ac)?ac:ac?.data||[]);
    setDispatches(Array.isArray(dis)?dis:dis?.data||[]);
    setCycles(Array.isArray(cyc)?cyc:cyc?.data||[]);
    setLoading(false);
  },[]);

  useEffect(()=>{load();},[load]);

  const openForm=(type:string,item?:any)=>{
    setFormType(type);setEditItem(item||null);
    const defs:any={instrument:defInstrument,set:defSet,tray:defTray,
      sterilization:defSterilization,autoclave:defAutoclave,dispatch:defDispatch,cycle:defCycle};
    setForm(item?{...item}:{...defs[type]||{}});
    setShowForm(true);
  };

  const saveForm=async()=>{
    const endpointMap:any={
      instrument:'/enterprise/cssd/instruments',
      set:'/enterprise/cssd/sets',
      tray:'/enterprise/cssd/trays',
      sterilization:'/enterprise/cssd/sterilization',
      autoclave:'/enterprise/cssd/autoclaves',
      dispatch:'/enterprise/cssd/dispatch',
      cycle:'/enterprise/cssd/cycles',
    };
    const ep=endpointMap[formType];
    const r=editItem
      ?await apiFetch(`${ep}/${editItem.id}`,'PUT',form)
      :await apiFetch(ep,'POST',form);
    if(r?.id||r?.data?.id||r?.success!==false){
      notify(editItem?`✅ ${formType} updated`:`✅ ${formType} created`);
      setShowForm(false);setEditItem(null);load();
    } else notify('❌ Failed — check backend');
  };

  const updateTrayStatus=async(trayId:string,status:string)=>{
    await apiFetch(`/enterprise/cssd/trays/${trayId}`,'PUT',{status});
    notify('✅ Tray status updated');load();
  };

  const returnDispatch=async(dispatchId:string)=>{
    await apiFetch(`/enterprise/cssd/dispatch/${dispatchId}/return`,'POST',{returnedAt:new Date().toISOString(),conditionOnReturn:'GOOD'});
    notify('✅ Return recorded');load();
  };

  // Stats
  const stats={
    totalInstruments:instruments.length,
    totalSets:sets.length,
    totalTrays:trays.length,
    sterileReady:trays.filter((t:any)=>t.status==='STERILIZED').length,
    dirty:trays.filter((t:any)=>t.status==='DIRTY').length,
    dispatched:trays.filter((t:any)=>t.status==='IN_USE').length,
    autoclaves:autoclaves.filter((a:any)=>a.is_active!==false).length,
    todayCycles:cycles.filter((c:any)=>c.cycle_date?.startsWith(new Date().toISOString().split('T')[0])).length,
  };

  const filtered=(arr:any[],fields:string[])=>arr.filter(item=>{
    if(!search)return true;
    const q=search.toLowerCase();
    return fields.some(f=>{const v=item[f]||item[f.replace(/_([a-z])/g,(_:any,c:string)=>c.toUpperCase())];return String(v||'').toLowerCase().includes(q);});
  });

  const TABS=[
    ['dashboard','📊 Dashboard'],['instruments','🔧 Instruments'],['sets','📦 Sets'],
    ['trays','🗂️ Trays'],['sterilization','♻️ Sterilization'],['autoclaves','⚙️ Autoclaves'],
    ['dispatch','🚚 Dispatch'],['tracking','📍 Tracking'],
  ];

  const FORM_TITLES:any={instrument:'Instrument',set:'Instrument Set',tray:'Tray',
    sterilization:'Sterilization Record',autoclave:'Autoclave',dispatch:'Dispatch',cycle:'Autoclave Cycle'};

  return(
    <Layout title="CSSD — Central Sterile Supply Department" actions={
      <div className="flex gap-2">
        {tab!=='dashboard'&&tab!=='tracking'&&(
          <button onClick={()=>openForm(tab==='trays'?'tray':tab==='sets'?'set':tab==='sterilization'?'sterilization':tab==='autoclaves'?'autoclave':tab==='dispatch'?'dispatch':tab==='instruments'?'instrument':'cycle')}
            className="px-4 py-1.5 rounded-lg text-sm font-semibold text-white" style={{background:'#02357A'}}>
            + Add {tab==='instruments'?'Instrument':tab==='sets'?'Set':tab==='trays'?'Tray':tab==='sterilization'?'Record':tab==='autoclaves'?'Autoclave':tab==='dispatch'?'Dispatch':'Cycle'}
          </button>
        )}
      </div>
    }>
      <div className="p-4">
        {msg&&<div className={`mb-4 p-3 rounded-xl text-sm font-medium border ${msg.startsWith('✅')?'bg-green-50 border-green-200 text-green-700':'bg-red-50 border-red-200 text-red-700'}`}>{msg}</div>}

        {/* Tabs */}
        <div className="flex gap-1 mb-5 bg-gray-100 p-1 rounded-xl overflow-x-auto">
          {TABS.map(([t,l])=>(
            <button key={t} onClick={()=>{setTab(t as any);setSearch('');}} className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${tab===t?'bg-white text-[#02357A] shadow font-semibold':'text-gray-600 hover:text-gray-800'}`}>{l}</button>
          ))}
        </div>

        {/* ── DASHBOARD ── */}
        {tab==='dashboard'&&(
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              {[
                ['Total Instruments',stats.totalInstruments,'#02357A','🔧'],
                ['Sterile Ready',stats.sterileReady,'#039089','✅'],
                ['Dirty / Pending',stats.dirty,'#dc2626','🔴'],
                ['In Use / Dispatched',stats.dispatched,'#d97706','🏥'],
                ['Instrument Sets',stats.totalSets,'#7c3aed','📦'],
                ['Total Trays',stats.totalTrays,'#576478','🗂️'],
                ['Active Autoclaves',stats.autoclaves,'#039089','⚙️'],
                ["Today's Cycles",stats.todayCycles,'#02357A','♻️'],
              ].map(([l,v,c,icon])=>(
                <div key={l as string} className="bg-white rounded-xl border p-4" style={{borderLeft:`4px solid ${c}`}}>
                  <p className="text-xs font-semibold text-gray-500 uppercase">{l}</p>
                  <p className="text-2xl font-bold mt-1" style={{color:c as string}}>{icon} {v}</p>
                </div>
              ))}
            </div>
            {/* Workflow */}
            <div className="bg-white rounded-xl border p-5 mb-4">
              <h3 className="font-bold text-gray-800 mb-4">CSSD Workflow</h3>
              <div className="flex items-center gap-2 overflow-x-auto pb-2">
                {[['🔴','Dirty','red'],['→','',''],['🧼','Decontamination','blue'],['→','',''],['🔬','Inspection','blue'],['→','',''],['📦','Packing','blue'],['→','',''],['♻️','Sterilization','teal'],['→','',''],['✅','Sterile Store','green'],['→','',''],['🚚','Dispatch','blue'],['→','',''],['🔄','Return & Re-process','yellow']].map(([icon,label,color],i)=>(
                  label?<div key={i} className={`flex flex-col items-center min-w-16 p-2 rounded-lg ${color==='green'?'bg-green-50':color==='teal'?'bg-teal-50':color==='red'?'bg-red-50':color==='yellow'?'bg-yellow-50':'bg-blue-50'}`}>
                    <span className="text-2xl">{icon}</span>
                    <span className="text-xs font-semibold text-gray-600 text-center mt-1">{label}</span>
                  </div>:<span key={i} className="text-gray-300 text-xl">→</span>
                ))}
              </div>
            </div>
            {/* Recent dispatches */}
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="px-5 py-3 border-b font-semibold text-sm text-gray-700">Recent Dispatches</div>
              {dispatches.slice(0,5).length===0?<p className="text-center py-8 text-gray-400 text-sm">No dispatches yet</p>:(
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 border-b"><th className="px-4 py-2 text-left text-xs text-gray-500">Tray</th><th className="px-4 py-2 text-left text-xs text-gray-500">Dispatched To</th><th className="px-4 py-2 text-left text-xs text-gray-500">{t('common.time','Time')}</th><th className="px-4 py-2 text-left text-xs text-gray-500">{t('common.status','Status')}</th></tr></thead>
                  <tbody className="divide-y">{dispatches.slice(0,5).map((d:any)=>(
                    <tr key={d.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium">{d.tray_id||d.trayId||'—'}</td>
                      <td className="px-4 py-2">{d.dispatched_to||d.dispatchedTo||'—'}</td>
                      <td className="px-4 py-2 text-xs text-gray-400">{d.dispatched_at?.replace('T',' ').slice(0,16)||'—'}</td>
                      <td className="px-4 py-2"><BADGE label={d.returned_at?'RETURNED':'DISPATCHED'} color={d.returned_at?'gray':'blue'}/></td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* ── INSTRUMENTS ── */}
        {tab==='instruments'&&(
          <>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search instruments..." className="w-full border rounded-xl px-4 py-2.5 text-sm mb-4 focus:outline-none"/>
            {filtered(instruments,['instrument_code','instrument_name','category']).length===0?(
              <div className="text-center py-16 bg-white rounded-2xl border">
                <p className="text-5xl mb-3">🔧</p><p className="font-bold text-gray-700">No instruments added</p>
                <p className="text-gray-400 text-sm mb-5">Add surgical instruments to track sterilization</p>
                <button onClick={()=>openForm('instrument')} className="px-5 py-2.5 rounded-xl text-white font-semibold text-sm" style={{background:'#02357A'}}>+ Add Instrument</button>
              </div>
            ):(
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-max">
                    <thead><tr style={{background:'#02357A'}}>{['Code','Instrument Name','Category','Material','Count','Status','Actions'].map(h=><th key={h} className="px-4 py-3 text-left text-xs font-bold text-white">{h}</th>)}</tr></thead>
                    <tbody className="divide-y">
                      {filtered(instruments,['instrument_code','instrument_name','category']).map((ins:any,i:number)=>(
                        <tr key={ins.id} className={i%2===0?'':'bg-gray-50/50'}>
                          <td className="px-4 py-3 font-mono text-xs font-bold text-[#02357A]">{ins.instrument_code||ins.instrumentCode}</td>
                          <td className="px-4 py-3 font-semibold">{ins.instrument_name||ins.instrumentName}</td>
                          <td className="px-4 py-3"><BADGE label={ins.category||'—'} color="blue"/></td>
                          <td className="px-4 py-3 text-xs">{ins.material||'—'}</td>
                          <td className="px-4 py-3 font-bold text-center">{ins.total_count||ins.totalCount||0}</td>
                          <td className="px-4 py-3"><BADGE label={ins.is_active!==false?'Active':'Inactive'} color={ins.is_active!==false?'green':'gray'}/></td>
                          <td className="px-4 py-3"><button onClick={()=>openForm('instrument',{...ins,instrumentCode:ins.instrument_code||ins.instrumentCode,instrumentName:ins.instrument_name||ins.instrumentName,totalCount:String(ins.total_count||ins.totalCount||1),isActive:ins.is_active!==false})} className="px-3 py-1 rounded text-xs bg-gray-50 hover:bg-gray-100">✏️ Edit</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── SETS ── */}
        {tab==='sets'&&(
          <>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search sets..." className="w-full border rounded-xl px-4 py-2.5 text-sm mb-4 focus:outline-none"/>
            {sets.length===0?(
              <div className="text-center py-16 bg-white rounded-2xl border">
                <p className="text-5xl mb-3">📦</p><p className="font-bold text-gray-700">No instrument sets</p>
                <p className="text-gray-400 text-sm mb-5">Group instruments into sets by procedure type</p>
                <button onClick={()=>openForm('set')} className="px-5 py-2.5 rounded-xl text-white font-semibold text-sm" style={{background:'#02357A'}}>+ Add Set</button>
              </div>
            ):(
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {filtered(sets,['set_code','set_name','procedure_type']).map((s:any)=>(
                  <div key={s.id} className="bg-white rounded-xl border shadow-sm p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div><p className="font-bold text-gray-900">{s.set_name||s.setName}</p><p className="text-xs text-gray-400 font-mono">{s.set_code||s.setCode}</p></div>
                      <BADGE label={s.is_active!==false?'Active':'Inactive'} color={s.is_active!==false?'green':'gray'}/>
                    </div>
                    <div className="flex gap-2 mb-3 flex-wrap">
                      {(s.procedure_type||s.procedureType)&&<BADGE label={s.procedure_type||s.procedureType} color="purple"/>}
                      {(s.sterilization_method||s.sterilizationMethod)&&<BADGE label={s.sterilization_method||s.sterilizationMethod} color="teal"/>}
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Trays: {trays.filter((t:any)=>(t.set_id||t.setId)===s.id).length}</p>
                    <button onClick={()=>openForm('set',{...s,setCode:s.set_code||s.setCode,setName:s.set_name||s.setName,procedureType:s.procedure_type||s.procedureType||'',sterilizationMethod:s.sterilization_method||s.sterilizationMethod||'AUTOCLAVE',isActive:s.is_active!==false})} className="w-full py-1.5 rounded-lg text-xs bg-gray-50 hover:bg-gray-100 font-medium">✏️ Edit</button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── TRAYS ── */}
        {tab==='trays'&&(
          <>
            <div className="flex gap-3 mb-4">
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search trays..." className="flex-1 border rounded-xl px-4 py-2.5 text-sm focus:outline-none"/>
              <div className="flex gap-2 text-xs">
                {(['CLEAN','DIRTY','STERILIZED','IN_USE','QUARANTINE'] as const).map(s=>(
                  <span key={s} className={`px-2 py-1 rounded-full font-semibold ${s==='CLEAN'?'bg-green-100 text-green-700':s==='DIRTY'?'bg-red-100 text-red-700':s==='STERILIZED'?'bg-teal-100 text-teal-700':s==='IN_USE'?'bg-yellow-100 text-yellow-700':'bg-purple-100 text-purple-700'}`}>
                    {s}: {trays.filter((t:any)=>t.status===s).length}
                  </span>
                ))}
              </div>
            </div>
            {trays.length===0?(
              <div className="text-center py-16 bg-white rounded-2xl border">
                <p className="text-5xl mb-3">🗂️</p><p className="font-bold text-gray-700">No trays</p>
                <button onClick={()=>openForm('tray')} className="mt-4 px-5 py-2.5 rounded-xl text-white font-semibold text-sm" style={{background:'#02357A'}}>+ Add Tray</button>
              </div>
            ):(
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {filtered(trays,['tray_code','tray_name','status','current_location']).map((t:any)=>{
                  const status=t.status||'CLEAN';
                  return(
                    <div key={t.id} className="bg-white rounded-xl border shadow-sm p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div><p className="font-bold text-gray-900">{t.tray_name||t.trayName||t.tray_code||t.trayCode}</p><p className="text-xs text-gray-400 font-mono">{t.tray_code||t.trayCode}</p></div>
                        <BADGE label={status} color={STATUS_COLOR[status]||'gray'}/>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">📍 {t.current_location||t.currentLocation||'CSSD'}</p>
                      <select value={status} onChange={e=>updateTrayStatus(t.id,e.target.value)} className="w-full border rounded-lg px-2 py-1.5 text-xs mb-2 bg-white focus:outline-none">
                        {TRAY_STATUSES.map(s=><option key={s}>{s}</option>)}
                      </select>
                      <button onClick={()=>openForm('tray',{...t,setId:t.set_id||t.setId||'',trayCode:t.tray_code||t.trayCode,trayName:t.tray_name||t.trayName,currentLocation:t.current_location||t.currentLocation||'CSSD'})} className="w-full py-1.5 rounded-lg text-xs bg-gray-50 hover:bg-gray-100">✏️ Edit</button>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ── STERILIZATION ── */}
        {tab==='sterilization'&&(
          <>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search records..." className="w-full border rounded-xl px-4 py-2.5 text-sm mb-4 focus:outline-none"/>
            {sterilizations.length===0?(
              <div className="text-center py-16 bg-white rounded-2xl border">
                <p className="text-5xl mb-3">♻️</p><p className="font-bold text-gray-700">No sterilization records</p>
                <button onClick={()=>openForm('sterilization')} className="mt-4 px-5 py-2.5 rounded-xl text-white font-semibold text-sm" style={{background:'#02357A'}}>+ Add Record</button>
              </div>
            ):(
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-max">
                    <thead><tr style={{background:'#02357A'}}>{['Tray','Autoclave','Cycle #','Load #','Sterilization Date','Expiry Date','Status'].map(h=><th key={h} className="px-4 py-3 text-left text-xs font-bold text-white">{h}</th>)}</tr></thead>
                    <tbody className="divide-y">
                      {sterilizations.map((s:any,i:number)=>(
                        <tr key={s.id} className={i%2===0?'':'bg-gray-50/50'}>
                          <td className="px-4 py-3 font-medium">{s.tray_id||s.trayId||'—'}</td>
                          <td className="px-4 py-3 text-xs">{s.autoclave_id||s.autoclaveId||'—'}</td>
                          <td className="px-4 py-3 font-mono text-xs">{s.cycle_number||s.cycleNumber||'—'}</td>
                          <td className="px-4 py-3 font-mono text-xs">{s.load_number||s.loadNumber||'—'}</td>
                          <td className="px-4 py-3 text-xs">{s.sterilization_date?.slice(0,16)||s.sterilizationDate?.slice(0,16)||'—'}</td>
                          <td className="px-4 py-3 text-xs">{s.expiry_date||s.expiryDate||'—'}</td>
                          <td className="px-4 py-3"><BADGE label={s.status||'PENDING'} color={STATUS_COLOR[s.status||'PENDING']||'yellow'}/></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── AUTOCLAVES ── */}
        {tab==='autoclaves'&&(
          <>
            {autoclaves.length===0?(
              <div className="text-center py-16 bg-white rounded-2xl border">
                <p className="text-5xl mb-3">⚙️</p><p className="font-bold text-gray-700">No autoclaves registered</p>
                <button onClick={()=>openForm('autoclave')} className="mt-4 px-5 py-2.5 rounded-xl text-white font-semibold text-sm" style={{background:'#02357A'}}>+ Add Autoclave</button>
              </div>
            ):(
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {autoclaves.map((a:any)=>{
                  const nextMaint=a.next_maintenance||a.nextMaintenance;
                  const isOverdue=nextMaint&&new Date(nextMaint)<new Date();
                  return(
                    <div key={a.id} className={`bg-white rounded-xl border shadow-sm p-4 ${isOverdue?'border-red-300':''}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div><p className="font-bold text-gray-900">{a.autoclave_name||a.autoclaveName}</p><p className="text-xs text-gray-400 font-mono">{a.autoclave_code||a.autoclaveCode}</p></div>
                        <BADGE label={a.is_active!==false?'Active':'Inactive'} color={a.is_active!==false?'green':'gray'}/>
                      </div>
                      <div className="space-y-1 text-xs text-gray-600 mb-3">
                        {a.brand&&<p>🏭 Brand: {a.brand}</p>}
                        {(a.capacity_units||a.capacityUnits)&&<p>📦 Capacity: {a.capacity_units||a.capacityUnits} units</p>}
                        <p className={isOverdue?'text-red-600 font-bold':''}>🔧 Next Maintenance: {nextMaint||'Not set'}{isOverdue?' ⚠️ OVERDUE':''}</p>
                        <p>Today's Cycles: <strong>{cycles.filter((c:any)=>(c.autoclave_id||c.autoclaveId)===a.id&&c.cycle_date?.startsWith(new Date().toISOString().split('T')[0])).length}</strong></p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={()=>openForm('cycle',{autoclaveId:a.id,cycleNumber:'',cycleDate:new Date().toISOString().slice(0,16),temperature:'134',pressure:'2.2',durationMinutes:'18',cycleResult:'PASS'})} className="flex-1 py-1.5 rounded-lg text-xs font-medium bg-teal-50 text-[#039089] hover:bg-teal-100">♻️ Record Cycle</button>
                        <button onClick={()=>openForm('autoclave',{...a,autoclaveCode:a.autoclave_code||a.autoclaveCode,autoclaveName:a.autoclave_name||a.autoclaveName,capacityUnits:String(a.capacity_units||a.capacityUnits||''),lastMaintenance:a.last_maintenance||a.lastMaintenance||'',nextMaintenance:nextMaint||'',isActive:a.is_active!==false})} className="px-3 py-1.5 rounded-lg text-xs bg-gray-50 hover:bg-gray-100">✏️</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Cycle log */}
            {cycles.length>0&&(
              <div className="mt-5 bg-white rounded-xl border overflow-hidden">
                <div className="px-5 py-3 border-b font-semibold text-sm text-gray-700">Recent Autoclave Cycles</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-max">
                    <thead><tr className="bg-gray-50 border-b">{['Cycle #','Date','Temp (°C)','Pressure','Duration','Result'].map(h=><th key={h} className="px-4 py-2 text-left text-xs text-gray-500 font-semibold">{h}</th>)}</tr></thead>
                    <tbody className="divide-y">
                      {cycles.slice(0,10).map((c:any,i:number)=>(
                        <tr key={c.id} className={i%2===0?'':'bg-gray-50/50'}>
                          <td className="px-4 py-2 font-mono text-xs">{c.cycle_number||c.cycleNumber}</td>
                          <td className="px-4 py-2 text-xs">{c.cycle_date?.slice(0,16)||c.cycleDate?.slice(0,16)}</td>
                          <td className="px-4 py-2 text-xs">{c.temperature||'—'}°C</td>
                          <td className="px-4 py-2 text-xs">{c.pressure||'—'} bar</td>
                          <td className="px-4 py-2 text-xs">{c.duration_minutes||c.durationMinutes||'—'} min</td>
                          <td className="px-4 py-2"><BADGE label={c.cycle_result||c.cycleResult||'—'} color={c.cycle_result==='PASS'||c.cycleResult==='PASS'?'green':'red'}/></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── DISPATCH ── */}
        {tab==='dispatch'&&(
          <>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search dispatches..." className="w-full border rounded-xl px-4 py-2.5 text-sm mb-4 focus:outline-none"/>
            {dispatches.length===0?(
              <div className="text-center py-16 bg-white rounded-2xl border">
                <p className="text-5xl mb-3">🚚</p><p className="font-bold text-gray-700">No dispatches</p>
                <button onClick={()=>openForm('dispatch')} className="mt-4 px-5 py-2.5 rounded-xl text-white font-semibold text-sm" style={{background:'#02357A'}}>+ New Dispatch</button>
              </div>
            ):(
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-max">
                    <thead><tr style={{background:'#02357A'}}>{['Tray','Dispatched To','Dispatched At','Required By','Status','Actions'].map(h=><th key={h} className="px-4 py-3 text-left text-xs font-bold text-white">{h}</th>)}</tr></thead>
                    <tbody className="divide-y">
                      {dispatches.map((d:any,i:number)=>(
                        <tr key={d.id} className={i%2===0?'':'bg-gray-50/50'}>
                          <td className="px-4 py-3 font-medium">{d.tray_id||d.trayId||'—'}</td>
                          <td className="px-4 py-3">{d.dispatched_to||d.dispatchedTo||'—'}</td>
                          <td className="px-4 py-3 text-xs">{d.dispatched_at?.slice(0,16)||d.dispatchedAt?.slice(0,16)||'—'}</td>
                          <td className="px-4 py-3 text-xs">{d.required_by?.slice(0,16)||d.requiredBy?.slice(0,16)||'—'}</td>
                          <td className="px-4 py-3"><BADGE label={d.returned_at?'RETURNED':'OUT'} color={d.returned_at?'gray':'blue'}/></td>
                          <td className="px-4 py-3">{!d.returned_at&&<button onClick={()=>returnDispatch(d.id)} className="px-3 py-1 rounded text-xs bg-green-50 text-green-700 hover:bg-green-100 font-medium">↩️ Return</button>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── TRACKING ── */}
        {tab==='tracking'&&(
          <div>
            <p className="text-sm text-gray-500 mb-4">Full audit trail of all CSSD activities</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border p-5">
                <h3 className="font-bold text-gray-800 mb-4">Tray Status Overview</h3>
                {TRAY_STATUSES.map(s=>{
                  const count=trays.filter((t:any)=>t.status===s).length;
                  const pct=trays.length?Math.round(count/trays.length*100):0;
                  return(
                    <div key={s} className="mb-3">
                      <div className="flex justify-between text-xs mb-1"><span className="font-semibold">{s}</span><span className="text-gray-500">{count} trays ({pct}%)</span></div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div className="h-2 rounded-full transition-all" style={{width:`${pct}%`,background:s==='STERILIZED'?'#039089':s==='DIRTY'?'#dc2626':s==='IN_USE'?'#d97706':s==='CLEAN'?'#16a34a':'#7c3aed'}}/>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="bg-white rounded-xl border p-5">
                <h3 className="font-bold text-gray-800 mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <button onClick={()=>{setTab('trays');}} className="w-full py-2.5 rounded-xl border text-sm font-medium hover:bg-gray-50 text-left px-4">🔴 View Dirty Trays ({trays.filter((t:any)=>t.status==='DIRTY').length})</button>
                  <button onClick={()=>{setTab('sterilization');}} className="w-full py-2.5 rounded-xl border text-sm font-medium hover:bg-gray-50 text-left px-4">♻️ Record Sterilization</button>
                  <button onClick={()=>{setTab('dispatch');}} className="w-full py-2.5 rounded-xl border text-sm font-medium hover:bg-gray-50 text-left px-4">🚚 Dispatch Tray ({trays.filter((t:any)=>t.status==='STERILIZED').length} ready)</button>
                  <button onClick={()=>{openForm('cycle');setTab('autoclaves');}} className="w-full py-2.5 rounded-xl border text-sm font-medium hover:bg-gray-50 text-left px-4">⚙️ Log Autoclave Cycle</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── MODAL FORM ── */}
        {showForm&&(
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={()=>setShowForm(false)}/>
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
              <div className="px-6 py-4 flex items-center justify-between flex-shrink-0" style={{background:'linear-gradient(135deg,#02357A,#015CA6)'}}>
                <h3 className="font-bold text-white">{editItem?`Edit ${FORM_TITLES[formType]}`:`Add ${FORM_TITLES[formType]}`}</h3>
                <button onClick={()=>setShowForm(false)} className="text-white/70 hover:text-white font-bold text-lg">✕</button>
              </div>
              <div className="p-6 overflow-y-auto space-y-4">

                {/* Instrument form */}
                {formType==='instrument'&&<>
                  <div className="grid grid-cols-2 gap-3">
                    <FLD label="Instrument Code" required><INP value={form.instrumentCode||''} onChange={(e:any)=>f('instrumentCode',e.target.value.toUpperCase())} placeholder="INS-001"/></FLD>
                    <FLD label="Instrument Name" required><INP value={form.instrumentName||''} onChange={(e:any)=>f('instrumentName',e.target.value)} placeholder="Scissors, Forceps..."/></FLD>
                    <FLD label="Category"><SEL value={form.category||'SURGICAL'} onChange={(e:any)=>f('category',e.target.value)}>{INSTRUMENT_CATEGORIES.map(c=><option key={c}>{c}</option>)}</SEL></FLD>
                    <FLD label="Material"><SEL value={form.material||'STAINLESS_STEEL'} onChange={(e:any)=>f('material',e.target.value)}><option value="STAINLESS_STEEL">Stainless Steel</option><option value="TITANIUM">Titanium</option><option value="CARBON_FIBRE">Carbon Fibre</option><option value="PLASTIC">Plastic</option></SEL></FLD>
                    <FLD label="Total Count"><INP type="number" value={form.totalCount||'1'} onChange={(e:any)=>f('totalCount',e.target.value)}/></FLD>
                    <div className="flex items-center gap-2 pt-5"><input type="checkbox" checked={form.isActive!==false} onChange={e=>f('isActive',e.target.checked)} className="w-4 h-4 rounded"/><label className="text-sm font-medium">{t('common.active','Active')}</label></div>
                  </div>
                  <FLD label="Description"><INP value={form.description||''} onChange={(e:any)=>f('description',e.target.value)} placeholder="Optional description"/></FLD>
                </>}

                {/* Set form */}
                {formType==='set'&&<>
                  <div className="grid grid-cols-2 gap-3">
                    <FLD label="Set Code" required><INP value={form.setCode||''} onChange={(e:any)=>f('setCode',e.target.value.toUpperCase())} placeholder="SET-001"/></FLD>
                    <FLD label="Set Name" required><INP value={form.setName||''} onChange={(e:any)=>f('setName',e.target.value)} placeholder="General Surgery Set"/></FLD>
                    <FLD label="Procedure Type"><INP value={form.procedureType||''} onChange={(e:any)=>f('procedureType',e.target.value)} placeholder="Laparoscopy, C-Section..."/></FLD>
                    <FLD label="Sterilization Method"><SEL value={form.sterilizationMethod||'AUTOCLAVE'} onChange={(e:any)=>f('sterilizationMethod',e.target.value)}>{STERILIZATION_METHODS.map(m=><option key={m}>{m}</option>)}</SEL></FLD>
                    <div className="flex items-center gap-2 pt-5"><input type="checkbox" checked={form.isActive!==false} onChange={e=>f('isActive',e.target.checked)} className="w-4 h-4 rounded"/><label className="text-sm font-medium">{t('common.active','Active')}</label></div>
                  </div>
                </>}

                {/* Tray form */}
                {formType==='tray'&&<>
                  <div className="grid grid-cols-2 gap-3">
                    <FLD label="Tray Code" required><INP value={form.trayCode||''} onChange={(e:any)=>f('trayCode',e.target.value.toUpperCase())} placeholder="TRAY-001"/></FLD>
                    <FLD label="Tray Name"><INP value={form.trayName||''} onChange={(e:any)=>f('trayName',e.target.value)} placeholder="General Surgery Tray 1"/></FLD>
                    <FLD label="Instrument Set"><SEL value={form.setId||''} onChange={(e:any)=>f('setId',e.target.value)}><option value="">No set assigned</option>{sets.map((s:any)=><option key={s.id} value={s.id}>{s.set_name||s.setName}</option>)}</SEL></FLD>
                    <FLD label="Status"><SEL value={form.status||'CLEAN'} onChange={(e:any)=>f('status',e.target.value)}>{TRAY_STATUSES.map(s=><option key={s}>{s}</option>)}</SEL></FLD>
                    <FLD label="Current Location"><INP value={form.currentLocation||'CSSD'} onChange={(e:any)=>f('currentLocation',e.target.value)} placeholder="CSSD, OT-1, Ward-3"/></FLD>
                  </div>
                </>}

                {/* Sterilization form */}
                {formType==='sterilization'&&<>
                  <div className="grid grid-cols-2 gap-3">
                    <FLD label="Tray" required><SEL value={form.trayId||''} onChange={(e:any)=>f('trayId',e.target.value)}><option value="">Select Tray</option>{trays.map((t:any)=><option key={t.id} value={t.id}>{t.tray_name||t.trayName||t.tray_code||t.trayCode}</option>)}</SEL></FLD>
                    <FLD label="Autoclave"><SEL value={form.autoclaveId||''} onChange={(e:any)=>f('autoclaveId',e.target.value)}><option value="">Select Autoclave</option>{autoclaves.map((a:any)=><option key={a.id} value={a.id}>{a.autoclave_name||a.autoclaveName}</option>)}</SEL></FLD>
                    <FLD label="Cycle Number"><INP value={form.cycleNumber||''} onChange={(e:any)=>f('cycleNumber',e.target.value)} placeholder="CYC-001"/></FLD>
                    <FLD label="Load Number"><INP value={form.loadNumber||''} onChange={(e:any)=>f('loadNumber',e.target.value)} placeholder="LOAD-001"/></FLD>
                    <FLD label="Sterilization Date"><INP type="datetime-local" value={form.sterilizationDate||''} onChange={(e:any)=>f('sterilizationDate',e.target.value)}/></FLD>
                    <FLD label="Expiry Date"><INP type="date" value={form.expiryDate||''} onChange={(e:any)=>f('expiryDate',e.target.value)}/></FLD>
                  </div>
                </>}

                {/* Autoclave form */}
                {formType==='autoclave'&&<>
                  <div className="grid grid-cols-2 gap-3">
                    <FLD label="Autoclave Code" required><INP value={form.autoclaveCode||''} onChange={(e:any)=>f('autoclaveCode',e.target.value.toUpperCase())} placeholder="AUTO-01"/></FLD>
                    <FLD label="Autoclave Name" required><INP value={form.autoclaveName||''} onChange={(e:any)=>f('autoclaveName',e.target.value)} placeholder="Autoclave 1"/></FLD>
                    <FLD label="Brand"><INP value={form.brand||''} onChange={(e:any)=>f('brand',e.target.value)} placeholder="Tuttnauer, MELAG..."/></FLD>
                    <FLD label="Capacity (units)"><INP type="number" value={form.capacityUnits||''} onChange={(e:any)=>f('capacityUnits',e.target.value)} placeholder="10"/></FLD>
                    <FLD label="Last Maintenance"><INP type="date" value={form.lastMaintenance||''} onChange={(e:any)=>f('lastMaintenance',e.target.value)}/></FLD>
                    <FLD label="Next Maintenance"><INP type="date" value={form.nextMaintenance||''} onChange={(e:any)=>f('nextMaintenance',e.target.value)}/></FLD>
                    <div className="flex items-center gap-2 pt-5"><input type="checkbox" checked={form.isActive!==false} onChange={e=>f('isActive',e.target.checked)} className="w-4 h-4 rounded"/><label className="text-sm font-medium">{t('common.active','Active')}</label></div>
                  </div>
                </>}

                {/* Autoclave Cycle form */}
                {formType==='cycle'&&<>
                  <div className="grid grid-cols-2 gap-3">
                    <FLD label="Autoclave" required><SEL value={form.autoclaveId||''} onChange={(e:any)=>f('autoclaveId',e.target.value)}><option value="">Select Autoclave</option>{autoclaves.map((a:any)=><option key={a.id} value={a.id}>{a.autoclave_name||a.autoclaveName}</option>)}</SEL></FLD>
                    <FLD label="Cycle Number"><INP value={form.cycleNumber||''} onChange={(e:any)=>f('cycleNumber',e.target.value)} placeholder="CYC-001"/></FLD>
                    <FLD label="Cycle Date"><INP type="datetime-local" value={form.cycleDate||''} onChange={(e:any)=>f('cycleDate',e.target.value)}/></FLD>
                    <FLD label="Temperature (°C)"><INP type="number" value={form.temperature||'134'} onChange={(e:any)=>f('temperature',e.target.value)}/></FLD>
                    <FLD label="Pressure (bar)"><INP type="number" value={form.pressure||'2.2'} onChange={(e:any)=>f('pressure',e.target.value)} placeholder="2.2"/></FLD>
                    <FLD label="Duration (min)"><INP type="number" value={form.durationMinutes||'18'} onChange={(e:any)=>f('durationMinutes',e.target.value)}/></FLD>
                    <FLD label="Cycle Result"><SEL value={form.cycleResult||'PASS'} onChange={(e:any)=>f('cycleResult',e.target.value)}><option value="PASS">PASS ✅</option><option value="FAIL">FAIL ❌</option><option value="ABORT">ABORT</option></SEL></FLD>
                  </div>
                </>}

                {/* Dispatch form */}
                {formType==='dispatch'&&<>
                  <div className="grid grid-cols-2 gap-3">
                    <FLD label="Tray" required><SEL value={form.trayId||''} onChange={(e:any)=>f('trayId',e.target.value)}><option value="">Select Sterile Tray</option>{trays.filter((t:any)=>t.status==='STERILIZED').map((t:any)=><option key={t.id} value={t.id}>{t.tray_name||t.trayName||t.tray_code||t.trayCode}</option>)}</SEL></FLD>
                    <FLD label="Dispatch To" required><INP value={form.dispatchedTo||''} onChange={(e:any)=>f('dispatchedTo',e.target.value)} placeholder="OT-1, Emergency, Ward-3"/></FLD>
                    <FLD label="Required By"><INP type="datetime-local" value={form.requiredBy||''} onChange={(e:any)=>f('requiredBy',e.target.value)}/></FLD>
                    <FLD label="Notes"><INP value={form.notes||''} onChange={(e:any)=>f('notes',e.target.value)} placeholder="Additional notes"/></FLD>
                  </div>
                </>}

              </div>
              <div className="px-6 py-4 border-t flex gap-3 flex-shrink-0">
                <button onClick={()=>setShowForm(false)} className="flex-1 py-2.5 border rounded-xl text-gray-600 text-sm hover:bg-gray-50">{t('common.cancel','Cancel')}</button>
                <button onClick={saveForm} className="flex-1 py-2.5 rounded-xl text-white font-semibold text-sm" style={{background:'#02357A'}}>{editItem?'Update':'Save'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
